import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, ShoppingCart, CheckCircle, Shield, Zap, ArrowRight, Star } from 'lucide-react';
import Footer from '../components/Footer';
import * as api from '../api/educentialApi';

const ScratchCards = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await api.getAllScratchCardProducts();
      setProducts(response.data || response || []);
    } catch (err) {
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { id: 'all', name: 'All Cards', count: products.length },
    { id: 'SSCE', name: 'SSCE', count: products.filter(c => c.category === 'SSCE').length },
    { id: 'Admission', name: 'Admission', count: products.filter(c => c.category === 'Admission').length },
    { id: 'Technical', name: 'Technical', count: products.filter(c => c.category === 'Technical').length },
    { id: 'BECE', name: 'BECE', count: products.filter(c => c.category === 'BECE').length }
  ];

  const benefits = [
    {
      icon: Zap,
      title: 'Instant Delivery',
      description: 'Get your scratch card PIN instantly via email and SMS after payment'
    },
    {
      icon: Shield,
      title: 'Secure Payment',
      description: 'Safe and encrypted payment processing with multiple payment options'
    },
    {
      icon: CheckCircle,
      title: 'Verified PINs',
      description: 'All PINs are verified and guaranteed to work for result checking'
    },
    {
      icon: CreditCard,
      title: '24/7 Support',
      description: 'Round-the-clock customer support for any issues or questions'
    }
  ];

  const addToCart = (product) => {
    const existingItem = cart.find(item => item.id === product.id);
    if (existingItem) {
      setCart(cart.map(item =>
        item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
      ));
    } else {
      setCart([...cart, { ...product, quantity: 1 }]);
    }
  };

  const proceedToCheckout = () => {
    if (cart.length === 0) {
      alert('Your cart is empty');
      return;
    }
    localStorage.setItem('scratchCardCart', JSON.stringify(cart));
    navigate('/checkout/scratch-cards');
  };

  const filteredProducts = selectedCategory === 'all'
    ? products
    : products.filter(p => p.category === selectedCategory);

  const totalCartItems = cart.reduce((sum, item) => sum + item.quantity, 0);
  const totalAmount = cart.reduce((sum, item) => sum + (Number(item.price) * item.quantity), 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">

      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-gray-50 to-white pt-20 pb-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
              Buy Exam Result
              <span className="text-yellow-500"> Checker Cards</span>
            </h1>
            <p className="text-xl text-gray-600 mb-10 leading-relaxed">
              Purchase WAEC, NECO, JAMB, NABTEB and other exam result checking scratch cards online. Instant delivery to your email and phone.
            </p>

            <div className="flex flex-wrap justify-center items-center gap-8 text-sm text-gray-600">
              <div className="flex items-center">
                <Shield className="h-5 w-5 text-green-500 mr-2" />
                <span>Secure Payment</span>
              </div>
              <div className="flex items-center">
                <Zap className="h-5 w-5 text-yellow-500 mr-2" />
                <span>Instant Delivery</span>
              </div>
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-blue-500 mr-2" />
                <span>100% Verified</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="bg-white border-y border-gray-200 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-3 rounded-lg font-semibold transition-all duration-200 ${
                  selectedCategory === category.id
                    ? 'bg-yellow-500 text-white shadow-lg'
                    : 'bg-gray-50 text-gray-700 border border-gray-200 hover:border-gray-300'
                }`}
              >
                {category.name} ({category.count})
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">
              Available Scratch Cards
            </h2>
            {totalCartItems > 0 && (
              <button
                onClick={proceedToCheckout}
                className="flex items-center px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-lg shadow-lg transition-all duration-200"
              >
                <ShoppingCart className="h-5 w-5 mr-2" />
                Checkout ({totalCartItems} items - ₦{totalAmount.toLocaleString()})
              </button>
            )}
          </div>

          {filteredProducts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-xl text-gray-600">No products available in this category</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProducts.map((product) => {
                const inCart = cart.find(item => item.id === product.id);

                return (
                  <div
                    key={product.id}
                    className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
                  >
                    {/* Image */}
                    <div className="relative h-56 bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
                      {product.imageUrl ? (
                        <img
                          src={product.imageUrl}
                          alt={product.productName}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <CreditCard className="h-20 w-20 text-gray-400" />
                        </div>
                      )}
                      <div className="absolute top-4 right-4 px-3 py-1 bg-white rounded-full text-sm font-semibold text-gray-900">
                        Instant
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-8">
                      <div className="mb-6">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{product.productName}</h3>
                        <p className="text-sm text-gray-500 mb-2">{product.examBody}</p>
                        <p className="text-gray-600 leading-relaxed text-sm">{product.description}</p>
                      </div>

                      <div className="flex items-center justify-between mb-6">
                        <div className="text-3xl font-bold text-gray-900">₦{Number(product.price).toLocaleString()}</div>
                        <span className="text-sm text-green-600 font-semibold flex items-center">
                          <CheckCircle className="h-4 w-4 mr-1" />
                          In Stock
                        </span>
                      </div>

                      <button
                        onClick={() => addToCart(product)}
                        className="w-full bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center"
                      >
                        <ShoppingCart className="h-5 w-5 mr-2" />
                        {inCart ? `Add Another (${inCart.quantity} in cart)` : 'Add to Cart'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Features */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Buy from Educential?
            </h2>
            <p className="text-xl text-gray-600">
              Fast, secure, and reliable scratch card service
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="group p-8 rounded-2xl border border-gray-200 hover:border-yellow-500 hover:shadow-xl transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-yellow-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-yellow-500 transition-colors duration-300">
                    <Icon className="h-7 w-7 text-yellow-600 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Need Help Choosing the Right Card?
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
            Contact our support team for assistance with your purchase
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {cart.length > 0 ? (
              <button
                onClick={proceedToCheckout}
                className="inline-flex items-center justify-center px-8 py-4 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
              >
                Proceed to Checkout
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
            ) : (
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="inline-flex items-center justify-center px-8 py-4 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
              >
                Start Shopping
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
            )}
            <button
              className="inline-flex items-center justify-center px-8 py-4 bg-white hover:bg-gray-100 text-gray-900 font-semibold rounded-lg transition-all duration-200"
            >
              Contact Support
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ScratchCards;
